import { ButtonType, HorizontalOriginOption, InlineAlertType, InlineAlertWidget, TabStyles,VerticalOriginOption } from '@tableau/widgets';
import { InfoIcon } from '@tableau/widgets/lib/src/widgets/Tooltip/Tooltip';
import * as React from 'react';
import * as _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { ButtonWithSpinner } from '~src/common/react/components/ButtonWithSpinner';
import { ButtonWithUberDialog } from '~src/common/react/components/ButtonWithUberDialog';
import {
  ActionButtonLeftWide,
  openReactActionDialog,
  ReactActionDialogBodyProps,
} from '~src/commonInfra/dialogs/ReactActionDialog';
import { openReactConfirmationDialog, ReactConfirmationDialogOptions } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { CloseAndDismiss } from '~src/commonInfra/dialogs/ReactModalService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { cssClass } from '~src/style';
import { IGetActionInfoParams } from '~src/ts/ServerApi/requests/GetActionInfoRequest';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { IResourceGovernanceServerSettings, IResourceGovernanceSettingsKey, IResourceGovernanceSiteSetting, IResourceGovernanceSiteSettingUpdate, UpdateResourceGovernanceSiteSettingsRequest } from '../types/ResourceGovernance';
import { ConcurrentJobLimitRowProps, ResourceGovernanceLimitsRow, rowShouldAlert } from './ResourceGovernanceLimitsRow';

// Note: This whole file is extremely similar to ResourceGovernanceEditDefaultLimitsDialog
// but _just_ different enough that unifying their logic would make it 
// harder to maintain

export type ResourceGovernanceEditSiteLimitsDialogProps = {
  sitesSettingsList: IResourceGovernanceSiteSetting[];
  serverSettings: Omit<IResourceGovernanceServerSettings, 'nextResetAt'>;
} & Partial<IResourceGovernanceServerSettings>;

export type ResourceGovernanceEditSiteLimitsDialogState = {
  [K in IResourceGovernanceSettingsKey]: {
    noLimit: boolean;
    value: number;
  };
};

export type ResourceGovernanceEditSiteLimitsBodyProps =
  ReactActionDialogBodyProps<
    ResourceGovernanceEditSiteLimitsDialogProps,
    ResourceGovernanceEditSiteLimitsDialogState
  >;

const cssClassPrefix = 'resource-governance-dialog-edit-site';
const leftPad = cssClass(cssClassPrefix, 'left-pad', {
  paddingLeft: 20,
  verticalAlign: 'middle',
});
const flexApart = cssClass(cssClassPrefix, 'flex-apart', {
  paddingLeft: 20,
  width: 120,
  display: 'flex',
  justifyContent: 'space-between',
});
const tooltipStyle = cssClass(cssClassPrefix, 'tooltip', {
  width: 300,
  display: 'inline-block',
  whiteSpace: 'pre-line',
});
const flattenDictionaries = <T, R>(acc: R, current: T) => ({ ...acc, ...current }) as T & R;
const rowKeys: IResourceGovernanceSettingsKey[] = ['extractsConcurrencyLimit', 'subscriptionsConcurrencyLimit', 'flowsConcurrencyLimit', 'runtimeLimit'];

export const SiteLimitsDialogTestIds = {
  CancelButton: 'siteLimitsDialogCancel',
  RevertToDefaultButton: 'revertToDefaultLimits',
  SaveLimitsButton: 'saveLimits'
};

export function openRevertConfirmationDialog(close: () => void, props: ResourceGovernanceEditSiteLimitsDialogProps, dialogState: ResourceGovernanceEditSiteLimitsDialogState) {
  const revertAction = () => {
    const siteIds = props.sitesSettingsList.map(s => s.siteId);
    const rowValues = getRowValues(dialogState);
    const defaultParams: IResourceGovernanceSiteSettingUpdate = {
      ...rowValues,
      siteIds: siteIds,
      isDefault: true
    };
    close();
    return saveSiteLimitsSettings(defaultParams);
  };
  const dialogOptions: ReactConfirmationDialogOptions<IGetActionInfoParams, void> = {
    title: Messages.resourceGovernanceRevertToDefaultTitle(),
    message: Messages.resourceGovernanceRevertToDefaultMessage(),
    actionName: Messages.confirm(),
    paramsForAction: undefined,
    action: revertAction,
    dialogWidth: 600
  };
  const confirmationModal = openReactConfirmationDialog(dialogOptions);
  return confirmationModal.result;
}

function getRowValues(dialogState: ResourceGovernanceEditSiteLimitsDialogState) {
  return rowKeys
    .map(key => {
      // We need to pass in parameters as null instead of undefined so they're not deleted from the request
      const value = dialogState[key].noLimit ? null : dialogState[key].value;
      return { [key]: value };
    })
    // We have to do a cast here because the compiler can't keep up with us, despite the flatten func being fully typed
    .reduce(flattenDictionaries, {}) as { [K in IResourceGovernanceSettingsKey]: number | undefined };
}

export function ResourceGovernanceEditSiteLimitsBody(
  props: ResourceGovernanceEditSiteLimitsBodyProps
): JSX.Element {
  const concurrentJobHeaderRow = <tr key='concurrent-header'>
    <td><b>{Messages.resourceGovernanceConcurrentJobsLimitsHeader()}</b></td>
    <td className={leftPad}><b>{Messages.resourceGovernanceNoLimitValue()}</b></td>
    <td className={leftPad}><b>{Messages.resourceGovernanceConcurrencySiteLimitHeader()}</b></td>
    <td className={flexApart}>
      <b>{Messages.resourceGovernanceConcurrencyDeaultLimitsHeader()}</b>
      {<ButtonWithUberDialog
        renderButton={showDialog => <InfoIcon onClick={showDialog}/>}
        uberDialogContent={<span className={tooltipStyle}>
          {Messages.resourceGovernanceConcurrencyLimitsTooltip()}
          <a href='http://example.com'>{Messages.learnMore()}</a>
        </span>}
        anchorOrigin={{ horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.top }}
        stackedOrigin={{ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top }}
      />}
    </td>
  </tr>;
  const updateKeyFromInput = (key: IResourceGovernanceSettingsKey) => (value: string) => {
    const isValidNum = /^\d+\.?\d*$/.test(value);
    value === undefined
      ? props.updateDialogState({ [key]: { noLimit: true, value } })
      : props.updateDialogState({ [key]: { noLimit: false, value: isValidNum ? Number(value) : NaN } });
  };
  const concurrentRowsInfo: ConcurrentJobLimitRowProps[] = [{
    description: Messages.resourceGovernanceConcurrencyDescription(),
    limitType: Messages.resourceGovernanceConcurrencyExtractRefreshes(),
    update: updateKeyFromInput('extractsConcurrencyLimit'),
    value: props.dialogState.extractsConcurrencyLimit.value,
    originalValue: props.extractsConcurrencyLimit,
    defaultValue: props.serverSettings.extractsConcurrencyLimit,
    hasDefault: true,
    testId: 'extractsConcurrencyLimit',
  },
  {
    description: Messages.resourceGovernanceConcurrencyDescription(),
    limitType: Messages.resourceGovernanceConcurrencySubscriptions(),
    update: updateKeyFromInput('subscriptionsConcurrencyLimit'),
    value: props.dialogState.subscriptionsConcurrencyLimit.value,
    originalValue: props.subscriptionsConcurrencyLimit,
    defaultValue: props.serverSettings.subscriptionsConcurrencyLimit,
    hasDefault: true,
    testId: 'subscriptionsConcurrencyLimit',
  },
  {
    description: Messages.resourceGovernanceConcurrencyDescription(),
    limitType: Messages.resourceGovernanceConcurrencyFlows(),
    update: updateKeyFromInput('flowsConcurrencyLimit'),
    value: props.dialogState.flowsConcurrencyLimit.value,
    originalValue: props.flowsConcurrencyLimit,
    defaultValue: props.serverSettings.flowsConcurrencyLimit,
    hasDefault: true,
    testId: 'flowsConcurrencyLimit',
  }];
  const concurrentJobRows = concurrentRowsInfo.map(row => <ResourceGovernanceLimitsRow {...row} key={row.limitType} />);
  const dailyHeader = <tr key='daily-header'>
    <td><b>{Messages.resourceGovernanceConcurrencyDailyLimitsHeader()}</b></td>
  </tr>;
  const dailyRowsInfo: ConcurrentJobLimitRowProps[] = [{
    description: Messages.resourceGovernanceBackgrounderDescription(),
    limitType: Messages.resourceGovernanceConcurrencyBackgrounderHours(),
    update: updateKeyFromInput('runtimeLimit'),
    value: props.dialogState.runtimeLimit.value,
    originalValue: props.runtimeLimit,
    defaultValue: props.serverSettings.runtimeLimit,
    hasDefault: true,
    testId: 'runtimeLimit',
  }];
  const dailyRows = dailyRowsInfo.map(row => <ResourceGovernanceLimitsRow {...row} key={row.limitType} />);
  const anyRowAlerting = _.any(concurrentRowsInfo.concat(dailyRowsInfo), row => rowShouldAlert(row.value, row.originalValue));
  const alertExplanation = <InlineAlertWidget
    testId='decreased-limit-alert'
    type={InlineAlertType.Warning}
  >
    {Messages.resourceGovernanceConcurrencyDecreasedLimitAlert()}
  </InlineAlertWidget>;
  return <>
    <table><tbody>
      {concurrentJobHeaderRow}
      {concurrentJobRows}
      {dailyHeader}
      {dailyRows}
    </tbody></table>
    {anyRowAlerting && alertExplanation}
  </>;
}

export async function saveSiteLimitsSettings(
  updateParams: IResourceGovernanceSiteSettingUpdate
): Promise<void> {
  WebHttpClient.sendRequest(new UpdateResourceGovernanceSiteSettingsRequest(updateParams))
    .then((result: IResult) => {
      if (ResultHelper.hasErrors(result)) {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.resourceGovernanceSiteSettingsSaveFailed(),
          actionMessage: Messages.resourceGovernanceLearnMoreMessage(),
          actionHref: 'http://www.example.com'
        });
        return Promise.reject();
      }
      ToasterService.instance.pop({ type: ToastType.Success, text: Messages.resourceGovernanceSiteSettingsSaveSuccessful() });
      return Promise.resolve(result);
    })
    .catch(() => {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.resourceGovernanceSiteSettingsSaveFailed(),
        actionMessage: Messages.resourceGovernanceLearnMoreMessage(),
        actionHref: 'http://www.example.com'
      });
      return Promise.reject();
    });
}

export function openResourceGovernanceEditSiteLimitsDialog(props: ResourceGovernanceEditSiteLimitsDialogProps) {
  const initialDialogState = rowKeys
    .map(key => {
      const noLimit = _.any(props.sitesSettingsList.map(s => s[key] === undefined));
      const value = noLimit ? undefined : _.max(props.sitesSettingsList.map(s => s[key]));
      return {
        [key]: { noLimit, value }
      };
    })
    .reduce(flattenDictionaries, {});
  const initialBodyProps = rowKeys
    .map(key => {
      const noLimit = _.any(props.sitesSettingsList.map(s => s[key] === undefined));
      const value = noLimit ? undefined : _.max(props.sitesSettingsList.map(s => s[key]));
      return {
        [key]: value
      };
    })
    .reduce(flattenDictionaries, { ...props });
  const saveSiteLimitsAction = (props: ResourceGovernanceEditSiteLimitsBodyProps, dialogState: ResourceGovernanceEditSiteLimitsDialogState) => {
    const rowValues = getRowValues(dialogState);
    const updateParams: IResourceGovernanceSiteSettingUpdate = {
      ...rowValues,
      siteIds: props.sitesSettingsList.map(s => s.siteId),
      isDefault: false, // Is Default is only true if the user is hitting the reset button
    };
    return saveSiteLimitsSettings(updateParams);
  };
  const actionDisabled = (props: ResourceGovernanceEditSiteLimitsBodyProps, dialogState: ResourceGovernanceEditSiteLimitsDialogState) => {
    const rowInvalid = rowKeys.map(key => {
      const dialogSubState = dialogState[key];
      // Technically we never expect to see an infinite value here, but better safe than sorry
      const invalidValue = dialogSubState.value === undefined || isNaN(dialogSubState.value) || !isFinite(dialogSubState.value);
      return !dialogSubState.noLimit && invalidValue;
    });
    return _.any(rowInvalid);
  };
  const footerStyling: React.CSSProperties = {
    display: 'flex',
    marginTop: TabStyles.Spacing.LargeSpace,
    whiteSpace: 'nowrap'
  };
  const rightButtonsStyling: React.CSSProperties = {
    marginLeft: 'auto'
  };
  const footer = (closeAndDismiss: CloseAndDismiss<any>, props: ResourceGovernanceEditSiteLimitsBodyProps, state: ResourceGovernanceEditSiteLimitsDialogState): JSX.Element => {
    return (
      <div style={footerStyling}>
        <ButtonWithSpinner
          buttonType={ButtonType.Outline}
          clicked={() => openRevertConfirmationDialog(closeAndDismiss.close, props,state)}
          testId={SiteLimitsDialogTestIds.RevertToDefaultButton}
          title={Messages.resourceGovernanceRevertToDefault()}
        />
        <div style={rightButtonsStyling}>
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            clicked={() => closeAndDismiss.dismiss('cancel')}
            testId={SiteLimitsDialogTestIds.CancelButton}
            title={Messages.cancel()}
          />
          <span className={ActionButtonLeftWide}>
            <ButtonWithSpinner
              buttonType={ButtonType.Primary}
              clicked={() => {
                closeAndDismiss.close();
                saveSiteLimitsAction(props, state);
              }}
              testId={SiteLimitsDialogTestIds.SaveLimitsButton}
              title={Messages.save()}
              disabled={actionDisabled(props, state)}
            />
          </span>
        </div>
      </div>
    );
  };
  const modal = openReactActionDialog({
    title: Messages.resourceGovernanceConcurrencyCustomizeSiteLimits(),
    bodyComponent: ResourceGovernanceEditSiteLimitsBody,
    dialogWidth: 770,
    initialDialogState,
    initialBodyProps,
    actionName: Messages.save(),
    action: () => Promise.resolve(), // Custom footer will execute the actions
    actionDisabled,
    dismissDialogOnActionFailed: true,
    initiallyFocusedElement: (dialog => dialog.querySelector('input')),
    footerComponent: footer
  });

  return modal.result;
}